/**
 * Comprime una imagen en el navegador ANTES de subirla: la redimensiona al
 * lado máximo indicado y la re-codifica como JPEG. Una foto de cámara de
 * 4–8 MB queda en ~150–300 KB sin perder nitidez útil (para ver un cilindro
 * o una fisura). Reduce el tiempo de subida, el almacenamiento y la descarga.
 *
 * Respeta la orientación EXIF (las fotos de celular suelen venir rotadas).
 * Si algo falla, devuelve el archivo original como fallback.
 */
export async function comprimirImagen(
  file: File,
  opciones: { maxLado?: number; calidad?: number } = {},
): Promise<Blob> {
  const maxLado = opciones.maxLado ?? 1600;
  const calidad = opciones.calidad ?? 0.7;

  if (!file.type.startsWith('image/')) return file;

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const escala = Math.min(1, maxLado / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * escala));
    const h = Math.max(1, Math.round(bitmap.height * escala));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', calidad),
    );
    // Si la compresión no achicó nada (raro), nos quedamos con el original.
    if (!blob) return file;
    return blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}
